import mapValues from 'lodash/fp/mapValues';
import { generateMedia } from 'styled-media-query';

interface Color {
  [key: string]: string;
}

export const colorPalette: Color = {
  neutral0: '#000000',
  neutral10: '#222222',
  neutral50: '#75777A',
  neutral70: '#AAABAE',
  neutral80: '#C4C4C4',
  neutral95: '#F4F4F4',
  neutral99: '#FCFCFF',
  neutral100: '#FFFFFF',
  neutral350: '#5C5E61',
  neutralVariant20: '#2D3137',
  neutralVariant25: '#3a3c3d',
  neutralVariant40: '#5B5F66',
  neutralVariant60: '#808D9A',
  neutralVariant70: '#9CB1C7',
  neutralVariant90: '#DFE4E8',
  neutralVariant95: '#CBD1D6',
  neutralVariant99: '#F5F7FA',
  navy10: '#001C39',
  navy20: '#00356B',
  navy30: '#1D4B7A',
  brandNavy: '#0B274B',
  brandWarmWhite: '#F9F6F2',
  navy40: '#155FAA',
  navy50: '#387ABF',
  navy90: '#E9F1FF',
  navy95: '#E5EDF4',
  navy99: '#F0F5FA',
  navy975: '#DAE8F7',
  blue30: '#004E61',
  blue40: '#00677F',
  blue50: '#00829F',
  blue60: '#0097BE',
  blue65: '#349EB9',
  blue70: '#2EB9DE',
  blue80: '#00CBFF',
  blue90: '#41C5E7',
  blue93: '#97ebfe',
  blue98: '#EBFBFF',
  muiBlue60: '#0288D1',
  muiBlue80: '#03A9F4',
  green30: '#1B5E20',
  green95: '#EAFCF3',
  green40: '#00726F',
  green45: '#2E7D32',
  green60: '#4CAF50',
  green70: '#00A8A3',
  green90: '#40EBAD',
  orange35: '#8A3800',
  orange43: '#B45200',
  orange44: '#B85400',
  orange46: '#E65100',
  orange55: '#ED6C02',
  orange73: '#FF9800',
  orange80: '#FCC190',
  orange95: '#FDF0E6',
  red20: '#680003',
  red30: '#930006',
  red40: '#BA1B1B',
  red43: '#C62828',
  red45: '#D32F2F',
  red50: '#DD3730',
  red53: '#EF5350',
  red55: '#FF382E',
  red80: '#FFB4A9',
  red90: '#FFDAD4',
  red95: '#FFEDE9',
  yellow60: '#FFD60A',
  purple30: '#B01B8F',
};

export const colorTokens = {
  loginArtwork: {
    shadowOutline: 'rgba(9, 30, 66, 0.08)',
    shadowDepth: 'rgba(9, 30, 66, 0.25)',
    gradientStart: 'rgba(217, 217, 217, 0)',
    gradientMiddle: 'rgba(32, 32, 32, 0.34)',
    gradientEnd: 'rgba(0, 0, 0, 1)',
  },
  text: {
    dark: colorPalette.neutral10,
    medium: colorPalette.neutralVariant40,
    secondary: colorPalette.neutralVariant40,
    hover: colorPalette.navy40,
    hoverInactive: colorPalette.neutralVariant25,
    active: colorPalette.navy20,
    disabled: colorPalette.neutral70,
    contrast: colorPalette.neutral100,
  },
  surface: {
    neutral: colorPalette.neutralVariant99,
  },
  border: {
    subtle: colorPalette.neutralVariant90,
    default: colorPalette.neutralVariant95,
  },
  input: {
    border: colorPalette.neutralVariant60,
  },
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    focus: 'rgba(0, 0, 0, 0.12)',
  },
  primary: {
    main: colorPalette.navy30,
    light: colorPalette.navy40,
    dark: colorPalette.navy20,
    surface: colorPalette.navy99,
  },
  button: {
    navy: colorPalette.brandNavy,
    primaryHover: '#07182D',
    primaryPressed: '#030C17',
    disabled: '#97A2B2',
    hover: '#F2F3F5',
    pressed: '#E4E8EB',
    destructive: '#D8180B',
    destructiveHover: '#FCECEA',
    destructivePressed: '#710D06',
    destructiveMuted: '#F2B3AE',
  },
  navigation: {
    background: colorPalette.brandNavy,
  },
  secondary: {
    main: colorPalette.blue90,
    light: colorPalette.blue80,
    dark: colorPalette.blue60,
  },
  info: {
    main: colorPalette.muiBlue60,
    dark: colorPalette.navy20,
    light: colorPalette.muiBlue80,
  },
  success: {
    main: colorPalette.green45,
    dark: colorPalette.green30,
    light: colorPalette.green60,
  },
  warning: {
    main: colorPalette.orange55,
    dark: colorPalette.orange46,
    light: colorPalette.orange73,
    background: colorPalette.orange95,
  },
  error: {
    main: colorPalette.red43,
    dark: colorPalette.red30,
    light: colorPalette.red53,
    // Matches the Figma "RideIQ/error/4p" style — a translucent red surface tint
    // used behind conflicting-service cards. Same rgba-string pattern as `action.*` above.
    surface: 'rgba(211, 47, 47, 0.04)',
  },
  background: {
    default: colorPalette.neutral100,
    light: colorPalette.navy99,
  },
};

const font = {
  size: {
    base: 13,
    medium: 16,
    large: 24,
  },
  weight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

const breakpoints = {
  desktop: 820,
  tablet: 480,
  mobile: 0,
};

const mediaQuery = generateMedia(mapValues((v) => `${v}px`, breakpoints));

const headingHeights = {
  withTitle: 151,
  withTitleTabs: 201,
  withTitleTabsFilters: 291,
};

const tableHeadHeight = 76;

const mobileSidebarHeight = 68;

export const variables = {
  font,
  colorPalette,
  colorTokens,
  breakpoints,
  mediaQuery,
  headingHeights,
  tableHeadHeight,
  mobileSidebarHeight,
};
